import { ActionReducerMapBuilder, UnknownAction } from '@reduxjs/toolkit'
import { createReducer, isAnyOf } from '@reduxjs/toolkit/react'
import { Traceable } from '@repo/shared/dtos/assistant'
import { TimeType } from '@repo/shared/dtos/generic'
import type { AsyncData } from '@repo/ui/utils'
import { DataStatus, sortByKey } from '@repo/ui/utils'
import { useMemo } from 'react'

import { storeActions } from '@/store/actions'

type TaskType = {
  archivedAt?: TimeType
  content: string
  createdAt: TimeType
  dueDate?: TimeType
  id: string
  title: string
  updatedAt: TimeType
} & Traceable

export type Task = TaskType & {
  archivedAt?: TimeType
  content: string
  createdAt: TimeType
  dueDate?: TimeType
  id: string
  title: string
  updatedAt: TimeType
  beneficiaryId: string
  categories?: string[]
  cta?: {
    link: string
    name: string
  }
  durationInHours?: number
} & Traceable
export type TaskState = {
  current: AsyncData<Task[]>
}

export const taskInitialState: TaskState = {
  current: DataStatus.Uninitialized,
}

// Selectors
export const selectTasksCount = (state: { task: TaskState }): number => {
  if (!DataStatus.isFulfilled(state.task.current)) return 0
  const current = state.task.current.filter((task) => !task.archivedAt).sort(sortByKey('title'))
  return current.length
}

export const taskReducer: ((state: TaskState | undefined, action: UnknownAction) => TaskState) & {
  getInitialState: () => TaskState
} = createReducer(taskInitialState, (builder) => {
  builder.addCase(storeActions.cleanUpOrganization, () => {
    return taskInitialState
  })

  builder
    .addCase(
      storeActions.beneficiary.initBeneficiary.pending,
      (
        state,
        {
          meta: {
            arg: { isSameBeneficiary },
          },
        },
      ) => {
        if (isSameBeneficiary) return
        state.current = DataStatus.Loading
      },
    )
    .addCase(storeActions.beneficiary.initBeneficiary.rejected, (state) => {
      state.current = DataStatus.Error
    })
    .addCase(
      storeActions.beneficiary.initBeneficiary.fulfilled,
      (state, { payload: { tasks } }) => {
        state.current = tasks
      },
    )

  builder
    .addCase(
      storeActions.initOrganization.pending,
      (
        state,
        {
          meta: {
            arg: { isSameOrganization },
          },
        },
      ) => {
        if (isSameOrganization) return
        state.current = DataStatus.Loading
      },
    )
    .addCase(storeActions.initOrganization.rejected, (state) => {
      state.current = DataStatus.Error
    })

  builder.addCase(
    storeActions.task.delete.fulfilled,
    (
      state,
      {
        meta: {
          arg: { task },
        },
      },
    ) => {
      if (DataStatus.isFulfilled(state.current)) {
        state.current = state.current.filter((t) => t.id !== task.id)
      }
    },
  )

  // Matchers
  taskMatcherCases(builder)
})

function taskMatcherCases(builder: ActionReducerMapBuilder<TaskState>) {
  builder
    .addMatcher(
      isAnyOf(
        storeActions.task.archive.fulfilled,
        storeActions.task.unarchive.fulfilled,
        storeActions.task.update.fulfilled,
        storeActions.task.updateDueDate.fulfilled,
      ),
      (state, { payload: { task } }) => {
        if (DataStatus.isFulfilled(state.current)) {
          state.current = state.current.map((existingTask) => {
            return existingTask.id === task.id ? task : existingTask
          })
        } else state.current = [task]
      },
    )
    .addMatcher(
      isAnyOf(storeActions.task.restore.fulfilled, storeActions.task.create.fulfilled),
      (state, { payload: { task } }) => {
        if (!DataStatus.isFulfilled(state.current)) state.current = []
        state.current.push(task)
      },
    )
}

export const useTaskLists = <T extends Task>(
  tasks: T[],
): {
  tasksComingSoon: T[]
  tasksCount: number
  tasksDone: T[]
  tasksOverdue: T[]
  tasksWithoutDueDate: T[]
} => {
  return useMemo(() => {
    const current = tasks.filter((task) => !task.archivedAt).sort(sortByKey('title'))

    const now = Date.now()
    const tasksComingSoon: T[] = []
    const tasksOverdue: T[] = []
    const tasksWithoutDueDate: T[] = []
    for (const task of current) {
      if (task.dueDate) {
        if (task.dueDate < now) tasksOverdue.push(task)
        else tasksComingSoon.push(task)
      } else {
        tasksWithoutDueDate.push(task)
      }
    }

    return {
      tasksComingSoon: tasksComingSoon.sort(sortByKey('dueDate')),
      tasksCount: current.length,
      tasksDone: tasks.filter((task) => task.archivedAt).sort(sortByKey('archivedAt', 'desc')),
      tasksOverdue: tasksOverdue.sort(sortByKey('dueDate')),
      tasksWithoutDueDate: tasksWithoutDueDate.sort(sortByKey('title')),
    }
  }, [tasks])
}
